export interface SeriesConfig {
  dataKey: string;
  name: string;
  color: string;
}

export interface VisualizationConfig {
  type: "bar" | "line" | "pie" | "table";
  title: string;
  xAxis: string;
  series: SeriesConfig[];
}

export interface AttendanceStats {
  /** Overall totals computed server-side over ALL result rows -- the display
   *  rows are capped, so client-side summing would silently undercount. */
  enrolled: number;
  present: number;
  half_day: number;
  absent: number;
}

export interface InsightChart {
  /** Server-computed aggregated companion chart for detail results (e.g. per-RO
   *  attendance rates). Carries its OWN small dataset -- the main rows are
   *  detail-grained and unchartable directly. */
  config: VisualizationConfig;
  data: Record<string, unknown>[];
}

export interface AnalyticsQueryRequest {
  query: string;
}

export type SSEStage = "idle" | "thinking" | "sql" | "data" | "summary" | "error" | "clarify" | "done";

export interface AnalyticsState {
  stage: SSEStage;
  sqlQuery: string | null;
  data: Record<string, unknown>[] | null;
  rowCount: number | null;
  totalCount: number | null;   // real total when rowCount was capped at 1,000
  visualizationConfig: VisualizationConfig | null;
  summary: string | null;
  downloadUrl: string | null;
  error: string | null;
}

export const STAGE_LABELS: Record<SSEStage, string> = {
  idle: "",
  thinking: "Thinking…",
  sql: "Generating SQL…",
  data: "Querying database…",
  summary: "Summarising results…",
  error: "Error",
  clarify: "Needs more detail",
  done: "Done",
};

// ── School report ─────────────────────────────────────────────────────────────

export interface ReportMetric {
  label: string;
  value: number | null;
  /** Set where the particular is not a number -- "Not held", "Lapsed
   *  23-12-2025", "Optical fibre". Rendered in place of `value`. */
  text_value: string | null;
  unit: string;
  national_median: number | null;
  region_median: number | null;
  /** Share of all active KVs scoring lower. Always reads as "better than",
   *  so pair it with higher_is_better before wording a comparison. */
  percentile: number | null;
  /** "critical" is a statutory requirement lapsed or never held. It carries no
   *  percentile -- an expired certificate is not in the bottom quarter of
   *  anything -- and is always listed ahead of the ranked findings. */
  verdict: "critical" | "attention" | "strength" | "neutral" | "unknown";
  note: string;
  /** False where a smaller number is the good outcome (rooms out of use). */
  higher_is_better: boolean;
}

export interface ReportSection {
  key: string;
  title: string;
  facts: Record<string, unknown>;
  metrics: ReportMetric[];
  /** Why the section is empty -- an unreachable database reads very differently
   *  from a school that simply has no such record, so it is never silent. */
  unavailable: string | null;
}

export interface SchoolReport {
  school_id: number;
  school_name: string;
  region: string;
  sections: ReportSection[];
  needs_attention: ReportMetric[];
  strengths: ReportMetric[];
}

// ── Multi-part answers ────────────────────────────────────────────────────────

export interface QueryPart {
  question: string;
  sql_query: string;
  data: Record<string, unknown>[];
  row_count: number;
  visualization_config: VisualizationConfig | null;
  summary: string;
  download_url: string | null;
  /** Set when this part alone failed; the others still answered. */
  error: string | null;
}

// ── Chat message types ────────────────────────────────────────────────────────

export interface UserChatMessage {
  id: string;
  role: "user";
  query: string;
}

export interface AssistantChatMessage {
  id: string;
  role: "assistant";
  stage: SSEStage;
  sqlQuery: string | null;
  data: Record<string, unknown>[] | null;
  rowCount: number | null;
  totalCount: number | null;
  visualizationConfig: VisualizationConfig | null;
  summary: string | null;
  downloadUrl: string | null;
  error: string | null;
  /** Set when the copilot asked a question instead of answering (SSE "clarify"). */
  clarifyQuestion: string | null;
  /** Suggested one-click answers. May be empty -- the input box is the fallback. */
  clarifyOptions: string[];
  insightChart: InsightChart | null;
  stats: AttendanceStats | null;
  /** Server-side llm_chat_turn.id for this exchange, sent back as edit_turn_id
   *  to re-ask the question. null when the turn could not be stored or the API
   *  predates the feature -- editing then falls back to a fresh session. */
  turnId: number | null;
  /** When `data` was refilled by re-running the stored SQL, as epoch ms.
   *  Result rows are never persisted -- too large, and PII that would go stale
   *  while still looking current -- so a reopened chat has no table until the
   *  user asks for one. Non-null means the figures below were read at this
   *  time, NOT when the question was originally asked, and may disagree with
   *  the summary text above them. */
  rerunAt: number | null;
  rerunState: "idle" | "loading" | "error";
  rerunError: string | null;
  /** Set when the copilot answered with a whole-school report instead of a
   *  query. Not persisted: a reopened chat keeps the narrative (which is the
   *  summary) but not the card, since the report is rebuilt from live data. */
  report: SchoolReport | null;
  /** A conversational reply -- a greeting or a capability question. No query
   *  was generated and nothing was read, so there is no SQL, no table and no
   *  turn stored against the session. */
  message: string | null;
  /** "You could also ask" chips under the summary. Curated server-side from
   *  questions the pipeline is proven to answer, so clicking one always works.
   *  Empty on error/clarify/report answers. */
  followups: string[];
  /** The sub-questions the planner chose, shown while they are being answered. */
  plannedParts: string[] | null;
  /** One entry per sub-question, each with its own SQL and figures. Never
   *  combined -- two traceable answers beat one total nobody can check.
   *  Not persisted; the stored turn summary keeps the labelled transcript. */
  parts: QueryPart[] | null;
}

export type ChatMessage = UserChatMessage | AssistantChatMessage;

export interface PresetQuery {
  label: string;
  query: string;
  category: "attendance" | "scores" | "enrollment" | "ict";
}

export const PRESET_QUERIES: PresetQuery[] = [
  // Phrased with relative time only. Hardcoded dates go stale -- the previous set
  // still asked about 18-03-2026 months after the fact -- and a preset that
  // returns an empty result reads to the user as "the system is broken".
  { label: "Attendance today",      query: "Employee attendance today", category: "attendance" },
  { label: "Absent today",          query: "Which employees are absent today?", category: "attendance" },
  { label: "Attendance by region",  query: "Employee attendance rate by regional office this month", category: "attendance" },
  { label: "Weakest regions",       query: "Which regional offices have the lowest staff attendance this month?", category: "attendance" },

  { label: "Scores by class",       query: "Average cognitive scores by class", category: "scores" },
  { label: "Domain breakdown",      query: "Average student scores across all six domains", category: "scores" },
  { label: "Term comparison",       query: "Compare Term 1 and Term 2 cognitive scores", category: "scores" },
  { label: "At-risk by region",     query: "How many students are flagged at risk in each region?", category: "scores" },

  { label: "Enrolment by region",   query: "Total student enrolment by region", category: "enrollment" },
  { label: "Enrolment by class",    query: "How many students are enrolled in each class?", category: "enrollment" },
  { label: "Boys and girls",        query: "Boys and girls enrolment split by region", category: "enrollment" },
  { label: "Largest schools",       query: "Which schools have the largest student enrolment?", category: "enrollment" },

  { label: "ICT summary",           query: "National ICT summary - computers, labs, smart classrooms and internet coverage", category: "ict" },
  { label: "Smart classroom gap",   query: "Which regions have the most schools without smart classrooms?", category: "ict" },
  { label: "No internet",           query: "List schools with no internet connectivity", category: "ict" },
  { label: "PM-SHRI comparison",    query: "Compare ICT coverage between PM-SHRI and non-PM-SHRI schools", category: "ict" },
];
